import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Technology from "./components/Technology";
import UseCases from "./components/UseCases";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import RequestDemoModal from "./components/RequestDemoModal";
import RequestQuoteModal from "./components/RequestQuoteModal";
import { trackEvent, captureUtms } from "./lib/analytics";

// Product Pages
import UL101Page from "./pages/UL101Page";
import ThermalHubPage from './pages/ThermalHubPage';
import ThermalProductPage from './pages/ThermalProductPage';
import FotricAcousticPage from "./pages/FotricAcousticPage";
import AcousticCameraH4MiniPage from "./pages/AcousticCameraH4MiniPage";
import AcousticCameraHSeriesPage from "./pages/AcousticCameraHSeriesPage";
import AcousticCameraHFlexPage from "./pages/AcousticCameraHFlexPage";
import AcousticCameraH7Page from "./pages/AcousticCameraH7Page";
import DalcommIntercomPage from "./pages/DalcommIntercomPage";
import DalcommHeadsetsPage from "./pages/DalcommHeadsetsPage";
import DalcommIntercomKitPage from "./pages/DalcommIntercomKitPage";
import DalcommEmbryRiddleHeadsetPage from "./pages/DalcommEmbryRiddleHeadsetPage";
import DalcommJ1HeadsetPage from "./pages/DalcommJ1HeadsetPage";
import DalcommVintageHeadsetPage from "./pages/DalcommVintageHeadsetPage";
import DalcommGSEAdaptersHubPage from "./pages/DalcommGSEAdaptersHubPage";
import DalcommGSEAccessoryPage from "./pages/DalcommGSEAccessoryPage";
import DistributionPage from "./pages/DistributionPage";
import InvestorsPage from "./pages/InvestorsPage";
import AboutPage from "./pages/AboutPage";
import NewsPage from "./pages/NewsPage";
import NewsArticlePage from "./pages/NewsArticlePage";
import PressKitPage from "./pages/PressKitPage";
import NewsStrip from "./components/NewsStrip";
import PartnerLogoRow from "./components/PartnerLogoRow";
import CalibrationRMAPage from "./pages/CalibrationRMAPage";
import DocumentationPage from "./pages/DocumentationPage";
import WarrantyPage from "./pages/WarrantyPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import UseCasePage from "./pages/UseCasePage";
import UseCasesIndexPage from "./pages/UseCasesIndexPage";
import CookieBanner from "./components/CookieBanner";
import CharlieEchoChat from "./components/charlie/CharlieEchoChat";
import SEO, { organizationJsonLd } from "./components/SEO";
import ComboKitPage from "./pages/ComboKitPage";
import ComboKitsHubPage from "./pages/ComboKitsHubPage";

function HomePage({ onRequestDemo, onRequestQuote }) {
  return (
    <>
      <SEO
        title="ctrl.aero — Component Troubleshooting at the Speed of Sound"
        description="Ultrasonic sensors and acoustic imaging cameras for aviation maintenance. Advanced airframe and powerplant troubleshooting solutions from CTRL Systems."
        canonical="https://ctrl.aero/"
        jsonLd={organizationJsonLd}
      />
      <Header 
        onRequestDemo={onRequestDemo}
        onRequestQuote={onRequestQuote}
      />
      <NewsStrip />
      <Hero 
        onRequestDemo={onRequestDemo}
        onRequestQuote={onRequestQuote}
        compactTop
      />
      
      {/* Subtle Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
      
      <Technology />
      
      {/* Subtle Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-blue-200/50 to-transparent"></div>
      
      <UseCases />
      
      <PartnerLogoRow />
      
      {/* Subtle Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
      
      <Contact initialFormType={null} />
      <Footer />
    </>
  );
}

// Native browsers don't scroll to a hash after a client-side route
// change. This helper watches location.hash and calls scrollIntoView on
// the matching element so links like `/#about` and `/#use-cases` work
// consistently from any page.  When the pathname changes and there is
// NO hash, we scroll to the top so footer/header navigation lands the
// visitor at the H1 of the new page instead of mid-page.
function ScrollToHash() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [hash, pathname]);
  return null;
}

function App() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  useEffect(() => {
    captureUtms();
  }, []);  const handleRequestDemo = (meta = {}) => {
    trackEvent('request_demo_click', meta);
    setIsDemoModalOpen(true);
  };

  const [quotePrefill, setQuotePrefill] = useState(null);
  const handleRequestQuote = (meta = {}) => {
    const { prefill, ...tracking } = meta;
    trackEvent('request_quote_click', tracking);
    setQuotePrefill(prefill || null);
    setIsQuoteModalOpen(true);
  };

  return (
    <BrowserRouter>
      <ScrollToHash />
      <div className="App">
        <Toaster position="top-right" />
        <RequestDemoModal open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen} />
        <RequestQuoteModal open={isQuoteModalOpen} onOpenChange={setIsQuoteModalOpen} prefill={quotePrefill} />
        <Routes>
          <Route path="/" element={
            <HomePage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/ul101" element={
            <UL101Page 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/combo-kits" element={<ComboKitsHubPage onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />} />
          <Route path="/products/combo-kits/:slug" element={<ComboKitPage onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />} />
          <Route path="/products/dalcomm" element={
            <DalcommHeadsetsPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/dalcomm/wireless-intercom-kit" element={
            <DalcommIntercomKitPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/dalcomm/embry-riddle-aviation-headset" element={
            <DalcommEmbryRiddleHeadsetPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/dalcomm/j1-carbon-fiber-pilots-headset" element={
            <DalcommJ1HeadsetPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/dalcomm/vintage-aviation-headset" element={
            <DalcommVintageHeadsetPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/dalcomm/gse-adapters" element={
            <DalcommGSEAdaptersHubPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/dalcomm/gse-adapters/:slug" element={
            <DalcommGSEAccessoryPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/thermal-acoustic" element={<ThermalHubPage category="thermal-acoustic" onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />} />
          <Route path="/products/thermal-acoustic/:slug" element={<ThermalProductPage category="thermal-acoustic" onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />} />
          <Route path="/products/thermal-cameras" element={<ThermalHubPage category="thermal-cameras" onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />} />
          <Route path="/products/thermal-cameras/:slug" element={<ThermalProductPage category="thermal-cameras" onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />} />
          <Route path="/products/fotric" element={
            <FotricAcousticPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/acoustic-cameras" element={
            <FotricAcousticPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/acoustic-cameras/h4mini" element={
            <AcousticCameraH4MiniPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/acoustic-cameras/h-series" element={
            <AcousticCameraHSeriesPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/acoustic-cameras/h-flex" element={
            <AcousticCameraHFlexPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/products/acoustic-cameras/h7" element={
            <AcousticCameraH7Page
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/distribution" element={
            <DistributionPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/about" element={<AboutPage onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />} />
          <Route path="/investors" element={
            <InvestorsPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/news" element={
            <NewsPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/news/:slug" element={
            <NewsArticlePage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/press" element={<PressKitPage onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />} />
          <Route path="/support/calibration-rma" element={
            <CalibrationRMAPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/support/documentation" element={
            <DocumentationPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/support/warranty" element={
            <WarrantyPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/legal/privacy" element={
            <PrivacyPolicyPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/legal/cookies" element={
            <CookiePolicyPage 
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
          <Route path="/use-cases" element={
            <UseCasesIndexPage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
          <Route path="/use-cases/:slug" element={
            <UseCasePage
              onRequestDemo={handleRequestDemo}
              onRequestQuote={handleRequestQuote}
            />
          } />
        </Routes>
        <CookieBanner />
        <CharlieEchoChat onRequestDemo={handleRequestDemo} onRequestQuote={handleRequestQuote} />
      </div>
    </BrowserRouter>
  );
}

export default App;
